/**
 * SupporterRoleNav — an in-page, horizontally-scrollable section tab bar for the
 * supporter role dashboards. Pills never wrap to a second row and never overlap:
 * on narrow viewports the row scrolls horizontally instead. The active pill is
 * scrolled into view on change. Keyboard accessible (arrow Left/Right, Home/End).
 *
 * This is intentionally an IN-PAGE nav — the global GovLayout sidebar stays
 * untouched, so other roles/pages are unaffected.
 */
import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'

export interface SupporterNavSection {
  key: string
  label: string
  icon: React.ComponentType<{ className?: string }>
}

export function SupporterRoleNav({
  sections,
  active,
  onChange,
}: {
  sections: SupporterNavSection[]
  active: string
  onChange: (key: string) => void
}) {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const activeRef = useRef<HTMLButtonElement>(null)

  // Keep the active pill visible inside the scroll container.
  useEffect(() => {
    const el = activeRef.current
    const container = scrollerRef.current
    if (!el || !container) return
    const elLeft = el.offsetLeft
    const elRight = elLeft + el.offsetWidth
    const viewLeft = container.scrollLeft
    const viewRight = viewLeft + container.clientWidth
    if (elLeft < viewLeft) container.scrollLeft = elLeft
    else if (elRight > viewRight) container.scrollLeft = elRight - container.clientWidth
  }, [active])

  const focusByOffset = (offset: number) => {
    const idx = sections.findIndex((s) => s.key === active)
    if (idx === -1) return
    const next = Math.max(0, Math.min(sections.length - 1, idx + offset))
    onChange(sections[next].key)
    // Focus after render
    requestAnimationFrame(() => {
      const btns = scrollerRef.current?.querySelectorAll<HTMLButtonElement>('button[data-nav-key]')
      btns?.[next]?.focus()
    })
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault()
        focusByOffset(1)
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault()
        focusByOffset(-1)
        break
      case 'Home':
        e.preventDefault()
        onChange(sections[0].key)
        break
      case 'End':
        e.preventDefault()
        onChange(sections[sections.length - 1].key)
        break
    }
  }

  return (
    <div
      ref={scrollerRef}
      role="tablist"
      aria-orientation="horizontal"
      onKeyDown={onKeyDown}
      className="flex gap-1.5 overflow-x-auto overflow-y-hidden no-scrollbar scroll-smooth -mx-1 px-1 py-0.5"
    >
      {sections.map((s) => {
        const Icon = s.icon
        const isActive = s.key === active
        return (
          <button
            key={s.key}
            ref={isActive ? activeRef : undefined}
            data-nav-key={s.key}
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(s.key)}
            className={cn(
              'flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-medium whitespace-nowrap flex-shrink-0 min-h-[40px] transition-colors',
              isActive
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
            )}
          >
            <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />
            <span className="truncate max-w-[160px]">{s.label}</span>
          </button>
        )
      })}
    </div>
  )
}